import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FaPlus, FaLink, FaCheckCircle, FaGavel } from 'react-icons/fa';
import { revalidationAPI, equipmentAPI, productsAPI, samplingPlansAPI } from '../services/api';
import useApprovalRights from '../hooks/useApprovalRights';
import { Modal } from '../components/Modals';
import { ActionsMenu } from '../components/ActionsMenu';
import StatusBadge from '../components/StatusBadge';
import FilterStatCards, { matchesGroup } from '../components/FilterStatCards';
import { FaHourglassHalf, FaSyncAlt } from 'react-icons/fa';
import TablePagination from '../components/TablePagination';

const getRows = (payload) => {
  if (Array.isArray(payload?.data?.data?.rows)) return payload.data.data.rows;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

// Any of these can trigger a QA review of whether the change needs revalidation. Free text
// beyond this list is fine too — QA still has to make the Full/Partial/None call either way.
const TRIGGER_TYPES = [
  'Equipment Change', 'Process/Method Change', 'Cleaning Agent Change', 'Formulation Change',
  'Facility/Utility Change', 'Analytical Method Change', 'Periodic Review', 'Other'
];

const STATUS_STYLE = {
  Pending:      { bg: 'var(--warning-bg)', color: 'var(--warning-text)', border: 'var(--warning-border)' },
  'In Progress':{ bg: 'var(--info-bg)',    color: 'var(--info-text)',    border: 'var(--info-border)' },
  Closed:       { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-border)' },
};

const Pill = ({ label, style }) => (
  <span style={{
    padding: '2px 10px', borderRadius: '999px', fontWeight: 700, fontSize: 'var(--fs-xs)',
    background: style.bg, color: style.color, border: `1px solid ${style.border}`, whiteSpace: 'nowrap'
  }}>
    {label}
  </span>
);

const EMPTY_TRIGGER = {
  trigger_type: TRIGGER_TYPES[0],
  equipment_id: '',
  product_id: '',
  train_id: '',
  trigger_description: '',
  justification: ''
};

const TRIGGER_GROUPS = [
  { key: 'pending', title: 'Pending QA Decision', icon: FaHourglassHalf, color: 'warning', match: (t) => (t.status || 'Pending') === 'Pending' },
  { key: 'progress', title: 'In Progress', icon: FaSyncAlt, color: 'info', match: (t) => t.status === 'In Progress' },
  { key: 'closed', title: 'Closed', icon: FaCheckCircle, color: 'success', match: (t) => t.status === 'Closed' },
];

const Revalidation = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [triggers, setTriggers] = useState([]);
  const [triggerCard, setTriggerCard] = useState('All');
  const [equipment, setEquipment] = useState([]);
  const [products, setProducts] = useState([]);
  const [trains, setTrains] = useState([]);
  const [samplingPlans, setSamplingPlans] = useState([]);

  const [newModalOpen, setNewModalOpen] = useState(false);
  const [newForm, setNewForm] = useState(EMPTY_TRIGGER);

  const [decideModal, setDecideModal] = useState(null); // { trigger, revalidation_type, comment, password }
  const [linkModal, setLinkModal] = useState(null); // { trigger, sampling_plan_id }
  const [closeModal, setCloseModal] = useState(null); // { trigger, comment, password }
  const [submitting, setSubmitting] = useState(false);

  // Whether revalidation is required (and, later, whether it can be closed) is a QA call —
  // mirrors the QA-stage approver used everywhere else in the app (canQaApprove).
  const { canFinal: canDecide } = useApprovalRights('revalidation.approval');

  const loadAll = async () => {
    try {
      setLoading(true);
      const [trigRes, equipRes, prodRes, trainsRes, planRes] = await Promise.all([
        revalidationAPI.getAll(), equipmentAPI.getAll(), productsAPI.getAll({ limit: 5000 }),
        equipmentAPI.getTrains(), samplingPlansAPI.getAll()
      ]);
      setTriggers(getRows(trigRes));
      setEquipment(getRows(equipRes));
      setProducts(getRows(prodRes));
      setTrains(getRows(trainsRes));
      setSamplingPlans(getRows(planRes).filter((p) => p.status === 'Approved'));
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load revalidation data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const refresh = async () => setTriggers(getRows(await revalidationAPI.getAll()));

  const equipmentLabel = (id) => {
    const eq = equipment.find((e) => String(e.equipment_id) === String(id));
    return eq ? `${eq.equipment_code || ''} ${eq.equipment_name || ''}`.trim() : '';
  };
  const productLabel = (id) => {
    const p = products.find((p) => String(p.product_id) === String(id));
    return p ? `${p.product_code || ''} ${p.product_name || ''}`.trim() : '';
  };
  const trainLabel = (id) => {
    const t = trains.find((t) => String(t.train_id) === String(id));
    return t ? `${t.train_code || ''} ${t.train_name || ''}`.trim() : '';
  };

  // ── New trigger (change log entry) ─────────────────────────────────────────
  const openNewTrigger = () => { setNewForm(EMPTY_TRIGGER); setNewModalOpen(true); };
  const saveNewTrigger = async () => {
    if (!newForm.trigger_type) return toast.warning('Trigger type is required');
    if (!newForm.trigger_description.trim()) return toast.warning('Describe the change');
    setSaving(true);
    try {
      await revalidationAPI.create(newForm);
      toast.success('Change logged — pending QA decision on revalidation requirement');
      await refresh();
      setNewModalOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to log change');
    } finally {
      setSaving(false);
    }
  };

  // ── QA decision: is revalidation required? ─────────────────────────────────
  const openDecide = (trigger) => setDecideModal({ trigger, revalidation_type: 'Full', comment: '', password: '' });
  const submitDecide = async () => {
    if (!decideModal.password) return toast.error('Password required');
    setSubmitting(true);
    try {
      await revalidationAPI.decide(decideModal.trigger.trigger_id, {
        revalidation_type: decideModal.revalidation_type, comment: decideModal.comment, password: decideModal.password
      });
      toast.success(decideModal.revalidation_type === 'None' ? 'Marked as not requiring revalidation — closed' : 'Revalidation required — link a worst-case Sampling Plan next');
      setDecideModal(null);
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Decision failed');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Link the worst-case Sampling Plan that will carry the 3-batch study ────
  const openLink = (trigger) => setLinkModal({ trigger, sampling_plan_id: trigger.sampling_plan_id ? String(trigger.sampling_plan_id) : '' });
  const submitLink = async () => {
    if (!linkModal.sampling_plan_id) return toast.warning('Select a Sampling Plan');
    setSubmitting(true);
    try {
      await revalidationAPI.linkPlan(linkModal.trigger.trigger_id, { sampling_plan_id: linkModal.sampling_plan_id });
      toast.success('Sampling Plan linked');
      setLinkModal(null);
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to link plan');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Close (gated server-side on 3 worst-case batches being QA-Approved) ────
  const openClose = (trigger) => setCloseModal({ trigger, comment: '', password: '' });
  const submitClose = async () => {
    if (!closeModal.password) return toast.error('Password required');
    setSubmitting(true);
    try {
      await revalidationAPI.close(closeModal.trigger.trigger_id, { comment: closeModal.comment, password: closeModal.password });
      toast.success('Revalidation closed');
      setCloseModal(null);
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Cannot close yet');
    } finally {
      setSubmitting(false);
    }
  };

  const trainOptionsForPlan = useMemo(() => samplingPlans, [samplingPlans]);

  const filteredTriggers = useMemo(() => triggers.filter((t) => matchesGroup(TRIGGER_GROUPS, triggerCard, t)), [triggers, triggerCard]);
  const [triggerPage, setTriggerPage] = useState(1);
  const [triggerPageSize, setTriggerPageSize] = useState(25);
  const triggerTotalPages = Math.max(1, Math.ceil(filteredTriggers.length / triggerPageSize));
  useEffect(() => { setTriggerPage(1); }, [triggerCard, triggerPageSize]);
  useEffect(() => { if (triggerPage > triggerTotalPages) setTriggerPage(triggerTotalPages); }, [triggerPage, triggerTotalPages]);
  const paginatedTriggers = filteredTriggers.slice((triggerPage - 1) * triggerPageSize, triggerPage * triggerPageSize);

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
        <div>
          <h2 className="page-title">Revalidation</h2>
          <p className="page-subtitle">
            Log any change to equipment, process, product or facility. QA decides whether it requires revalidation — if it does, it can only be closed once a worst-case Sampling Plan has QA-Approved results across 3 consecutive batches, covering every sampling location.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" type="button" onClick={openNewTrigger}><FaPlus /> Log Change</button>
      </div>

      <FilterStatCards groups={TRIGGER_GROUPS} records={triggers} value={triggerCard} onChange={setTriggerCard} totalTitle="Total Changes" totalIcon={FaLink} />

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Equipment / Product / Train</th>
                <th>Description</th>
                <th>Revalidation Required?</th>
                <th>Linked Study</th>
                <th>Batch Progress</th>
                <th>Status</th>
                <th style={{ width: '120px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTriggers.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-muted">{triggers.length === 0 ? 'No changes logged yet' : 'No changes match the selected filter'}</td></tr>
              ) : paginatedTriggers.map((trigger) => {
                const progress = trigger.progress || { batches_completed: 0, batches_required: 3, complete: false };
                const requiredLabel = trigger.revalidation_required == null ? 'Pending QA decision' : (trigger.revalidation_required ? `Yes (${trigger.revalidation_type})` : 'No');
                return (
                  <tr key={trigger.trigger_id}>
                    <td style={{ fontWeight: 600 }}>{trigger.trigger_type}</td>
                    <td style={{ fontSize: 'var(--fs-md)' }}>
                      {[equipmentLabel(trigger.equipment_id), productLabel(trigger.product_id), trainLabel(trigger.train_id)].filter(Boolean).join(' / ') || '-'}
                    </td>
                    <td style={{ fontSize: 'var(--fs-md)', maxWidth: '260px' }}>{trigger.trigger_description || '-'}</td>
                    <td>
                      <Pill
                        label={requiredLabel}
                        style={trigger.revalidation_required == null
                          ? STATUS_STYLE.Pending
                          : trigger.revalidation_required ? STATUS_STYLE['In Progress'] : STATUS_STYLE.Closed}
                      />
                    </td>
                    <td style={{ fontSize: 'var(--fs-md)' }}>{trigger.plan_code || <span style={{ color: 'var(--text-secondary)' }}>Not linked</span>}</td>
                    <td>
                      {trigger.revalidation_required ? (
                        <Pill
                          label={`${progress.batches_completed} / ${progress.batches_required} batches${progress.locations ? ` · ${progress.locations} locations` : ''}`}
                          style={progress.complete ? STATUS_STYLE.Closed : STATUS_STYLE['In Progress']}
                        />
                      ) : '-'}
                    </td>
                    <td><StatusBadge status={trigger.status || 'Pending'} /></td>
                    <td className="action-cell" style={{ width: '120px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        {canDecide && trigger.status === 'Pending' && (
                          <button className="btn btn-sm btn-success" onClick={() => openDecide(trigger)} title="QA Decision">
                            <FaGavel />
                          </button>
                        )}
                        {canDecide && trigger.status === 'In Progress' && !!trigger.sampling_plan_id && (
                          <button className="btn btn-sm btn-success" onClick={() => openClose(trigger)} title="Close Revalidation">
                            <FaCheckCircle />
                          </button>
                        )}
                        <ActionsMenu items={[
                          { label: 'Link Sampling Plan', icon: <FaLink />, onClick: () => openLink(trigger), show: trigger.status === 'In Progress' },
                        ]} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredTriggers.length > 0 && (
          <TablePagination total={filteredTriggers.length} page={triggerPage} pageSize={triggerPageSize} onPageChange={setTriggerPage} onPageSizeChange={(n) => { setTriggerPageSize(n); setTriggerPage(1); }} itemLabel="changes" />
        )}
      </div>

      {/* ── New Trigger Modal ─────────────────────────────────────────────── */}
      <Modal
        isOpen={newModalOpen}
        onClose={() => setNewModalOpen(false)}
        title="Log a System Change"
        size="md"
        footer={
          <>
            <button className="btn btn-outline" onClick={() => setNewModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveNewTrigger} disabled={saving}>{saving ? 'Saving...' : 'Log Change'}</button>
          </>
        }
      >
        <div className="modal-section">
          <h3 className="modal-section-title">Change Scope</h3>
          <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Trigger Type *</label>
            <select className="form-control" value={newForm.trigger_type} onChange={(e) => setNewForm((p) => ({ ...p, trigger_type: e.target.value }))}>
              {TRIGGER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Equipment</label>
            <select className="form-control" value={newForm.equipment_id} onChange={(e) => setNewForm((p) => ({ ...p, equipment_id: e.target.value }))}>
              <option value="">-</option>
              {equipment.map((eq) => <option key={eq.equipment_id} value={eq.equipment_id}>{eq.equipment_code} - {eq.equipment_name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Product</label>
            <select className="form-control" value={newForm.product_id} onChange={(e) => setNewForm((p) => ({ ...p, product_id: e.target.value }))}>
              <option value="">-</option>
              {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.product_code} - {p.product_name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Equipment Train</label>
            <select className="form-control" value={newForm.train_id} onChange={(e) => setNewForm((p) => ({ ...p, train_id: e.target.value }))}>
              <option value="">-</option>
              {trains.map((t) => <option key={t.train_id} value={t.train_id}>{t.train_code} - {t.train_name}</option>)}
            </select>
          </div>
          </div>
        </div>
        <div className="modal-section">
          <h3 className="modal-section-title">Description &amp; Impact</h3>
          <div className="form-group">
            <label className="form-label">Description of Change *</label>
            <textarea className="form-control" rows={2} value={newForm.trigger_description} onChange={(e) => setNewForm((p) => ({ ...p, trigger_description: e.target.value }))} placeholder="What changed?" />
          </div>
          <div className="form-group">
            <label className="form-label">Justification / Impact Assessment</label>
            <textarea className="form-control" rows={2} value={newForm.justification} onChange={(e) => setNewForm((p) => ({ ...p, justification: e.target.value }))} placeholder="Why might (or might not) this affect the validated cleaning state?" />
          </div>
        </div>
      </Modal>

      {/* ── QA Decision Modal ─────────────────────────────────────────────── */}
      {decideModal && (
        <Modal
          isOpen
          onClose={() => setDecideModal(null)}
          title="QA Decision — Is Revalidation Required?"
          size="sm"
          footer={
            <>
              <button className="btn btn-outline" onClick={() => setDecideModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitDecide} disabled={submitting}>{submitting ? 'Submitting...' : 'Confirm Decision'}</button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">Revalidation Type *</label>
            <select className="form-control" value={decideModal.revalidation_type} onChange={(e) => setDecideModal((p) => ({ ...p, revalidation_type: e.target.value }))}>
              <option value="Full">Full — Required</option>
              <option value="Partial">Partial — Required</option>
              <option value="None">None — Not Required</option>
            </select>
            <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
              {decideModal.revalidation_type === 'None'
                ? 'This closes the change immediately with no study needed.'
                : 'This moves the change to In Progress — it can only be closed once a linked Sampling Plan has QA-Approved results across the required number of worst-case batches (default 3; configurable in Admin Settings).'}
            </p>
          </div>
          <div className="form-group">
            <label className="form-label">Comment / Rationale</label>
            <textarea className="form-control" rows={2} value={decideModal.comment} onChange={(e) => setDecideModal((p) => ({ ...p, comment: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Login Password *</label>
            <input type="password" className="form-control" autoComplete="new-password" value={decideModal.password} onChange={(e) => setDecideModal((p) => ({ ...p, password: e.target.value }))} />
          </div>
        </Modal>
      )}

      {/* ── Link Plan Modal ────────────────────────────────────────────────── */}
      {linkModal && (
        <Modal
          isOpen
          onClose={() => setLinkModal(null)}
          title="Link Worst-Case Sampling Plan"
          size="sm"
          footer={
            <>
              <button className="btn btn-outline" onClick={() => setLinkModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitLink} disabled={submitting}>{submitting ? 'Linking...' : 'Link Plan'}</button>
            </>
          }
        >
          <p style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>
            Pick the Approved Sampling Plan (built for the worst-case product/train per Worst-Case Analysis) that will carry out this revalidation's 3-batch study. If none exists yet, create one in Sampling Plans first.
          </p>
          <div className="form-group">
            <label className="form-label">Sampling Plan *</label>
            <select className="form-control" value={linkModal.sampling_plan_id} onChange={(e) => setLinkModal((p) => ({ ...p, sampling_plan_id: e.target.value }))}>
              <option value="">Select plan</option>
              {trainOptionsForPlan.map((p) => (
                <option key={p.plan_id} value={p.plan_id}>{p.plan_code} - {p.train_name || 'No Train'} ({p.study_type})</option>
              ))}
            </select>
          </div>
        </Modal>
      )}

      {/* ── Close Modal ────────────────────────────────────────────────────── */}
      {closeModal && (
        <Modal
          isOpen
          onClose={() => setCloseModal(null)}
          title="Close Revalidation — Electronic Signature"
          size="sm"
          footer={
            <>
              <button className="btn btn-outline" onClick={() => setCloseModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitClose} disabled={submitting}>{submitting ? 'Closing...' : 'Confirm Close'}</button>
            </>
          }
        >
          <p style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>
            The server re-checks the linked plan's batch progress at submit time — this will be rejected if fewer than the required number of worst-case batches (default 3) have QA-Approved results across every location.
          </p>
          <div className="form-group">
            <label className="form-label">Closure Comment</label>
            <textarea className="form-control" rows={2} value={closeModal.comment} onChange={(e) => setCloseModal((p) => ({ ...p, comment: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Login Password *</label>
            <input type="password" className="form-control" autoComplete="new-password" value={closeModal.password} onChange={(e) => setCloseModal((p) => ({ ...p, password: e.target.value }))} />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Revalidation;
